import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, Cell } from "recharts";
import { Users, TrendingUp, FileText, Radar, Landmark, Calendar, Copy, Info, Link2 } from "lucide-react";
import { toast } from "sonner";

const BRAND_COLORS = ["#2f4a3a", "#d1704f", "#eaa385", "#d4b95c", "#8fa992", "#c9603d"];

function StatCard({ icon: Icon, label, value, sub, tooltip }) {
  return (
    <Card data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          {sub && <Badge variant="secondary" className="text-[10px]">{sub}</Badge>}
        </div>
        <div className="text-3xl font-heading font-bold tracking-tight">{value}</div>
        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
          {label}
          {tooltip && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="Info" className="text-muted-foreground hover:text-primary">
                    <Info className="w-3 h-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function DistBar({ title, data }) {
  const top = (data || []).slice(0, 10);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">{title}</CardTitle></CardHeader>
      <CardContent>
        {top.length === 0 ? (
          <div className="text-xs text-muted-foreground py-8 text-center">Nessun dato ancora</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, top.length * 32)}>
            <BarChart data={top} layout="vertical" margin={{ left: 20, right: 20, top: 4, bottom: 4 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11, fill: "hsl(155 15% 35%)" }} />
              <RTooltip
                cursor={{ fill: "hsl(155 27% 25% / 0.05)" }}
                contentStyle={{ background: "#fff", border: "1px solid #e6e0d3", borderRadius: 8, fontSize: 12 }}
              />
              <Bar dataKey="n" radius={[0, 6, 6, 0]}>
                {top.map((_, i) => <Cell key={i} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Feature: toggle "Includi tester" (default OFF)
  const [includeTesters, setIncludeTesters] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = { include_testers: includeTesters };
    Promise.all([api.get("/admin/stats", { params }), api.get("/admin/users", { params })])
      .then(([s, u]) => {
        setStats(s.data);
        setUsers(u.data.users);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Errore caricamento admin"))
      .finally(() => setLoading(false));
  }, [includeTesters]);

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    toast.success("Email copiata");
  };

  if (error) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center">
        <div className="text-2xl font-heading font-bold text-primary mb-2">Accesso negato</div>
        <div className="text-sm text-muted-foreground">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-bold mb-2">Admin</div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Il polso del beta.</h1>
          <p className="text-sm text-muted-foreground mt-2">Iscrizioni, attività e composizione della community TUOTAX in tempo reale.</p>
        </div>
        <div className="flex items-center gap-2 bg-muted/30 border rounded-full px-4 py-2" data-testid="admin-testers-toggle-wrapper">
          <Switch
            id="include-testers"
            checked={includeTesters}
            onCheckedChange={setIncludeTesters}
            data-testid="admin-testers-toggle"
          />
          <Label htmlFor="include-testers" className="text-xs font-semibold cursor-pointer">
            Includi tester
          </Label>
        </div>
      </div>

      {/* KPI cards */}
      {loading || !stats ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Utenti totali" value={stats.total_users} sub={`+${stats.users_today} oggi`} />
            <StatCard icon={TrendingUp} label="Ultimi 7 giorni" value={stats.users_week} />
            <StatCard icon={Radar} label="Bandi trovati" value={stats.total_bandi} />
            <StatCard icon={FileText} label="Documenti caricati" value={stats.total_docs} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard icon={Landmark} label="Transazioni importate" value={stats.total_transactions} />
            <StatCard icon={Calendar} label="Scadenze totali" value={stats.total_deadlines} />
            <StatCard
              icon={Users}
              label="Azioni per utente"
              value={
                stats.total_users > 0
                  ? Math.round(((stats.total_docs + stats.total_transactions + stats.total_deadlines) / stats.total_users) * 10) / 10
                  : 0
              }
              sub="doc + tx + scad"
              tooltip="Somma di documenti, transazioni e scadenze diviso il numero di utenti (nella vista corrente). Misura l'ampiezza d'uso medio, non il solo login."
            />
          </div>

          {/* Distribuzioni + Canale acquisizione */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DistBar title="Iscritti per canale (?invito=…)" data={stats.by_channel} />
            <DistBar title="Per Regione" data={stats.by_regione} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DistBar title="Tipo P.IVA" data={stats.by_tipo_piva} />
            <DistBar title="Settore" data={stats.by_settore} />
          </div>
        </>
      )}

      {/* Users table */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm font-heading">Ultimi iscritti ({users.length})</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
          ) : users.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Ancora nessun utente. Condividi tuotax.it 🚀</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-muted-foreground tracking-wider border-b border-border">
                    <th className="py-2 pr-3">Utente</th>
                    <th className="py-2 pr-3">Profilo</th>
                    <th className="py-2 pr-3">Canale</th>
                    <th className="py-2 pr-3 text-center">Doc</th>
                    <th className="py-2 pr-3 text-center">Bandi</th>
                    <th className="py-2 pr-3 text-center">Tx</th>
                    <th className="py-2 pr-3 text-center">Scad</th>
                    <th className="py-2 pr-3">Iscritto</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-border/40 hover:bg-accent/30 transition-colors" data-testid={`user-row-${u.email}`}>
                      <td className="py-3 pr-3">
                        <div className="font-semibold flex items-center gap-2">
                          {u.full_name || "—"}
                          {u.is_tester && <Badge variant="outline" className="text-[9px] py-0 px-1.5 rounded-full border-secondary/40 text-secondary">tester</Badge>}
                        </div>
                        <button
                          onClick={() => copyEmail(u.email)}
                          className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                          data-testid={`copy-email-${u.email}`}
                        >
                          {u.email} <Copy className="w-3 h-3" />
                        </button>
                      </td>
                      <td className="py-3 pr-3 text-xs text-muted-foreground">
                        {u.regione || "—"}<br />
                        {u.tipo_piva} · {u.settore || "—"}
                      </td>
                      <td className="py-3 pr-3 text-xs" data-testid={`user-channel-${u.email}`}>
                        {u.invite_code ? (
                          <span className="inline-flex items-center gap-1 text-primary font-mono bg-primary/5 border border-primary/20 rounded px-1.5 py-0.5">
                            <Link2 className="w-3 h-3" />{u.invite_code}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 pr-3 text-center font-mono text-xs">{u.activity.documents}</td>
                      <td className="py-3 pr-3 text-center font-mono text-xs">{u.activity.bandi}</td>
                      <td className="py-3 pr-3 text-center font-mono text-xs">{u.activity.transactions}</td>
                      <td className="py-3 pr-3 text-center font-mono text-xs">{u.activity.deadlines}</td>
                      <td className="py-3 pr-3 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(u.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
